import { useReducer } from 'react';
import { CheckBox, FormField, Select } from 'grommet';
import { Disclaimer } from '.';
import type { LanguagePreferenceProps } from '@customTypes/types';
import type { LanguagePreferenceState } from '@customTypes/stateTypes';
import { Languages, ChinaRegions } from '@customTypes/enums';

const supportBH = {
  [Languages.JA]: '08:30 - 19:30 (GMT+09:00)',
  [Languages.ES]: '09:00 - 00:00 (GMT+02:00)',
  [Languages.DE]: '09:00 - 17:00 (GMT+02:00)',
  [Languages.FR]: '09:00 - 17:00 (GMT+02:00)',
  [Languages.IT]: '09:00 - 17:00 (GMT+02:00)',
  [Languages.PT_BR]: 'TBD',
  [Languages.KO]: '09:00 - 18:00 (GMT+09:00)',
  [Languages.TR]: 'TBD',
  [Languages.ZH_CN]: '08:30 - 17:30 (GMT+08:00)',
  [Languages.ZH_TW]: {
    [ChinaRegions.HK]: '08:30 - 17:30 (GMT+08:00)',
    [ChinaRegions.TW]: '08:30 - 17:30 (GMT+08:00)',
  },
  [Languages.ID]: '08:00 - 17:00 (GMT+07:00)',
};

export const LanguagePreference = (props: LanguagePreferenceProps): JSX.Element => {
  const [state, updateState] = useReducer(
    (prev: LanguagePreferenceState, newState: Partial<LanguagePreferenceState>) => ({
      ...prev,
      ...newState,
    }),
    {
      language: Languages.EN,
      countryOrRegion: '',
      showCountryOrRegion: false,
      showNotification: false,
    }
  );

  const onPreferredLanguageChange = (option: string): void => {
    updateState({ language: option });

    switch (option) {
      case Languages.EN:
        updateState({ countryOrRegion: '', showCountryOrRegion: false, showNotification: false });
        break;
      case Languages.ZH_TW:
        updateState({ showCountryOrRegion: true, showNotification: false });
        break;
      default:
        updateState({ countryOrRegion: '', showCountryOrRegion: false, showNotification: true });
    }
  };

  const onCountryOrRegionChange = (option: string): void => {
    updateState({ countryOrRegion: option, showNotification: true });
  };

  const getSupportHours = (): string => {
    return !state.countryOrRegion ? supportBH[state.language] : supportBH[state.language][state.countryOrRegion] ;
  };

  const clearState = () => {
    updateState({
      language: Languages.EN,
      countryOrRegion: '',
      showCountryOrRegion: false,
      showNotification: false,
    });
  }

  props.onReset && props.onReset(clearState);

  return (
    <>
      <FormField
        name="preferredLanguage"
        htmlFor="preferred-language"
        label="Preferred language"
        required
      >
        <Select
          id="preferred-language"
          name="preferredLanguage"
          placeholder="Select"
          options={props.options}
          value={state.language}
          onChange={({ option }) => onPreferredLanguageChange(option)}
        />
      </FormField>

      {state.showCountryOrRegion &&
        <FormField
          name="countryOrRegion"
          htmlFor="country-region"
          label="Country or region"
          required
        >
          <Select
            id="country-region"
            name="countryOrRegion"
            placeholder="Select"
            options={Object.values(ChinaRegions)}
            value={state.countryOrRegion}
            onChange={({ option }) => onCountryOrRegionChange(option)}
          />
        </FormField>
      }

      {state.showNotification &&
        <>
          <Disclaimer
            text={
              <>
                Support Hours:<br />
                {state.language} : {getSupportHours()} during Business Hours (BH) only.<br />
                If you need immediate assistance during non-business hours, select English as your language.
              </>
            }
          />

          {props.supportHoursAcknowledgement &&
            <FormField
              name="supportHoursAcknowledgement"
              htmlFor="support-hours-acknowledgement"
              required
            >
              <CheckBox
                id="support-hours-acknowledgement"
                name="supportHoursAcknowledgement"
                label="I have read the support hours and acknowledge that there may be a delayed response if I submit a case outside of Business Hours or during a holiday period."
              />
            </FormField>
          }
        </>
      }
    </>
  );
};
